import { Bullet, Circle, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  DateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
} from "@amcharts/amcharts5/xy";
import { addYears, format, subYears } from "date-fns";
import { forIn, omit } from "lodash";

import { LegendData } from "@/classes/BaseChart";
import LineChart from "@/classes/LineChart";
import { getChartInterval } from "@/helpers/charts";
import { buildControlRows, buildFilterRows, buildHeaderRows } from "@/helpers/csvExport";
import { DATE_FORMAT, stringOrNumberToDate } from "@/helpers/formatters";
import { widgetApi } from "@/router";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { metricsDictionary } from "@/settings/metricsDictionary";
import { chartFontSettings, getAxisDateFormat } from "@/settings/widgets";
import store from "@/store";
import { ChartDataRecord } from "@/types/charts";
import { DashboardReportFilterParams } from "@/types/dashboard";

const PREVIOUS_YEAR_LABEL = " (previous)";

export default class extends LineChart {
  declare previousYearData: Record<string, string | number>[] | null;

  protected async getData(): Promise<void> {
    if (this.dataProvider) {
      const res = await this.dataProvider(this);
      this.data = this.mapData(res);

      if (this.settings.useExploreData) {
        const prevYearRes = (await this.dataProvider(this, { previousYear: true })) as ChartDataRecord[];
        this.previousYearData = prevYearRes?.length
          ? (this.mapData(this.mapPrevYearData(prevYearRes)) as Record<string, string | number>[])
          : null;
      } else {
        this.previousYearData = null;
      }

      store.commit("charts/updateNoDataStatus", {
        widgetId: this.widget.id,
        status: !this.data.length && !Object.keys(this.data).length,
      });
      return;
    }

    const mapDateRange = (filterParams: DashboardReportFilterParams) => {
      const { start_time: startDate, end_time: endDate } = filterParams;

      if (!startDate && !endDate) {
        return filterParams;
      }

      const startTime = format(subYears(stringOrNumberToDate(startDate as string), 1), DATE_FORMAT);
      const endTime = format(subYears(stringOrNumberToDate(endDate as string), 1), DATE_FORMAT);

      return {
        ...filterParams,
        start_time: startTime,
        end_time: endTime,
      };
    };

    const res = await widgetApi.getChartData(this);
    this.data = this.mapData(res);

    const params = store.getters["userSettings/filterParams"];
    const allTimeValue = "allTime";

    if (params?.dateRange?.value?.preset === allTimeValue) {
      this.previousYearData = null;
    } else {
      const prevYearRes = await widgetApi.getChartData(this, mapDateRange);
      this.previousYearData = this.mapData(this.mapPrevYearData(prevYearRes as ChartDataRecord[])) as Record<
        string,
        string | number
      >[];
    }

    store.commit("charts/updateNoDataStatus", {
      widgetId: this.widget.id,
      status: !this.data.length && !Object.keys(this.data).length && !Object.keys(this.previousYearData || {}).length,
    });
  }

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = [this.settings.yAxisPrimary];

    this.createCustomLineSeries(this.settings.yAxisPrimary, 0, this.settings.yAxisPrimary);

    if (this.previousYearData) {
      series.push(this.settings.yAxisPrimary);

      const prevYearTitle =
        metricsDictionary.find((metric) => {
          return metric.value === this.settings.yAxisPrimary;
        })?.title + PREVIOUS_YEAR_LABEL;
      this.createCustomLineSeries(this.settings.yAxisPrimary, 1, prevYearTitle);
    }

    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  protected mapPrevYearData(data: ChartDataRecord[]): ChartDataRecord[] {
    return data.map((item) => ({
      ...item,
      [this.settings.xAxis]: format(
        addYears(stringOrNumberToDate(item[this.settings.xAxis] as string), 1),
        DATE_FORMAT
      ),
    }));
  }

  protected mapData(data: ChartDataRecord[]) {
    const mappedData: ChartDataRecord[] = [];
    if (!Array.isArray(data)) {
      return [];
    }
    data.forEach((el) => {
      mappedData.push({
        [this.settings.xAxis]: el[this.settings.xAxis] === null ? 0 : el[this.settings.xAxis],
        [this.settings.yAxisPrimary]: el[this.settings.yAxisPrimary] === null ? 0 : el[this.settings.yAxisPrimary],
      });
    });
    return mappedData;
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 60 });
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const yPrimary = AxisRendererY.new(this.root, {});

    yPrimary.grid.template.setAll({
      strokeWidth: 1,
      strokeDasharray: [10, 5],
    });

    yPrimary.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.renderers = { x, yPrimary };
  }

  protected createAxes() {
    const isDay = this.settings.xAxis === "day";
    const isMonth = this.settings.xAxis === "month";
    const x = (this.chart as XYChart).xAxes.push(
      DateAxis.new(this.root, {
        renderer: this.renderers.x as AxisRendererX,
        groupData: true,
        groupIntervals: [this.interval],
        markUnitChange: false,
        baseInterval: {
          timeUnit: this.interval.timeUnit as time.TimeUnit,
          count: this.interval.count,
        },
        dateFormats: getAxisDateFormat(this.settings.xAxis),
      })
    );

    if (!isDay) {
      const loc = isMonth ? 0.5 : 0;
      x.get("renderer").labels.template.set("location", loc);
      x.get("renderer").grid.template.set("location", loc);
    }

    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          paddingBottom: 0,
          centerX: p50,
        })
      );
    }

    const yPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.yPrimary as AxisRendererY,
        numberFormat: this.getMetricFormatter(this.settings.yAxisPrimary, { notation: "compact" }),
      })
    );

    const yAxisPrimaryTitle = this.settings.yAxisPrimaryTitle;
    if (yAxisPrimaryTitle) {
      yPrimary.children.unshift(
        Label.new(this.root, {
          text: yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, yPrimary };
  }

  protected createCustomLineSeries(seriesName: string, index: number, legendLabel: string) {
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series = (this.chart as XYChart).series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.x,
        yAxis: this.axes.yPrimary,
        fill: this.getSeriesColor(index),
        minBulletDistance: 10,
        valueYField: seriesName,
        valueXField: this.settings.xAxis,
      })
    ) as LineSeries;

    if (this.options.showBullets) {
      series.bullets.push(() => {
        return Bullet.new(this.root, {
          sprite: Circle.new(this.root, {
            radius: 4,
            fill: series.get("fill"),
          }),
        });
      });
    }

    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = this.settings.yAxisPrimary;
    const metricFormatter = this.formatters?.[metricKey];
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        ...chartFontSettings.label,
        centerY: p50,
        dy: -16,
        dx: -32,
        populateText: true,
        fontSize: labelFontSize,
        text: "{valueY}",
      });

      label.adapters.add("text", (labelText, target) => {
        const rawValue = Number(target?.dataItem?.get("valueY" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return "";
        }

        if (!this.settings.showDataLabels) {
          return "";
        }

        return metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));
      });

      label.adapters.add("visible", (visible) => {
        return this.settings.showDataLabels ? visible : false;
      });

      return Bullet.new(this.root, {
        locationY: 1,
        sprite: label,
      });
    });

    this.createTooltip(series, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (key === this.settings.xAxis && index === 1) {
            param.push({ key, value: format(subYears(stringOrNumberToDate(value as string), 1), DATE_FORMAT) });
          } else {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    series.set("stroke", this.getSeriesColor(index));
    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    this.createLegendLabel(series, legendLabel);
    this.updateCustomLineSeriesData(seriesName, series, index);
  }

  protected updateCustomLineSeriesData(seriesName: string, series: LineSeries, index: number): void {
    const data = [] as ChartDataRecord[];

    ((index === 0 ? this.data : this.previousYearData) as ChartDataRecord[]).forEach((el: ChartDataRecord) => {
      const date = stringOrNumberToDate(el[this.settings.xAxis] as string).getTime();

      data.push({
        [this.settings.xAxis]: date,
        [seriesName]: el[seriesName],
      });
    });

    data.sort((a, b) => {
      return (a[this.settings.xAxis] as number) - (b[this.settings.xAxis] as number);
    });

    series.data.setAll(data);
  }

  exportData = (isSingleChart = true) => {
    let body = widgetApi.getChartRequestBody(this);
    body = omit(body, ["dimension", "top"]);

    const csvContent = [];
    csvContent.push(...buildHeaderRows(this.widget.title));
    csvContent.push(...buildFilterRows(body, isSingleChart));
    csvContent.push(...buildControlRows(this.controls));

    const metricName = metricsDictionary.find((metric) => metric.value === this.settings.yAxisPrimary)?.title;
    const headers = ["Date", metricName];
    if (this.previousYearData) {
      headers.push(`${metricName}${PREVIOUS_YEAR_LABEL}`);
    }

    csvContent.push([]);
    csvContent.push(headers);

    this.data.forEach((item: Record<string, unknown>) => {
      const row = [item[this.settings.xAxis], item[this.settings.yAxisPrimary]];

      if (this.previousYearData) {
        const prevYearItem = this.previousYearData.find(
          (prevYearItem) => prevYearItem[this.settings.xAxis] === item[this.settings.xAxis]
        );
        if (prevYearItem) {
          row.push(prevYearItem[this.settings.yAxisPrimary]);
        }
      }

      csvContent.push(row);
    });

    return csvContent;
  };
}
